import { type PairingDetail } from '@hungryroot/rest';
import styles from './utils.module.css';

const nutritionKeyToUnit = {
  total_fat_dv: '%',
  total_fat: 'g',
  sat_fat: 'g',
  sat_fat_dv: '%',
  trans_fat: 'g',
  cholesterol: 'mg',
  cholesterol_dv: '%',
  sodium: 'mg',
  sodium_dv: '%',
  total_carb: 'g',
  total_carb_dv: '%',
  fiber: 'g',
  fiber_dv: '%',
  sugars: 'g',
  added_sugars: 'g',
  added_sugars_dv: '%',
  protein: 'g',
  protein_dv: '%',
  vitamin_d: '%',
  calcium: '%',
  iron: '%',
  potassium: '%',
  vitamin_a: '%',
  vitamin_b6: '%',
  vitamin_c: '%',
  vitamin_e: '%',
};

const nutritionKeyToLabel = {
  calories: 'Calories per serving',
  total_fat: 'Total Fat',
  trans_fat: 'Trans Fat',
  sat_fat: 'Saturated Fat',
  cholesterol: 'Cholesterol',
  sodium: 'Sodium',
  total_carb: 'Total Carbohydrates',
  fiber: 'Dietary Fiber',
  sugars: 'Total Sugars',
  protein: 'Protein',
  vitamin_a: 'Vitamin A',
  vitamin_b6: 'Vitamin B6',
  vitamin_c: 'Vitamin C',
  vitamin_d: 'Vitamin D',
  vitamin_e: 'Vitamin E',
  calcium: 'Calcium',
  iron: 'Iron',
  potassium: 'Potassium',
};

export const stringNumberToNumberNoDecimal = (value: string) => {
  return Number(value).toFixed();
};

export const formatNutritionValue = (value: string | number, key: string) => {
  return `${stringNumberToNumberNoDecimal(String(value))}${
    nutritionKeyToUnit[key as keyof typeof nutritionKeyToUnit] || ''
  }`;
};

export const nutritionDataToNutritionTable = (
  nutritionData: PairingDetail['nutrition']
) => {
  const table = [
    {
      label: nutritionKeyToLabel.calories,
      secondSlotValue: formatNutritionValue(nutritionData.calories, 'calories'),
      className: styles['nutrition-row-border'],
    },
    {
      label: nutritionKeyToLabel.total_fat,
      firstSlotValue: formatNutritionValue(
        nutritionData.total_fat,
        'total_fat'
      ),
      secondSlotValue: formatNutritionValue(
        nutritionData.total_fat_dv,
        'total_fav_dv'
      ),
      subSections: [
        {
          label: nutritionKeyToLabel.sat_fat,
          firstSlotValue: formatNutritionValue(
            nutritionData.sat_fat,
            'sat_fat'
          ),
          secondSlotValue: formatNutritionValue(
            nutritionData.sat_fat_dv,
            'sat_fat_dv'
          ),
        },
        {
          label: nutritionKeyToLabel.trans_fat,
          firstSlotValue: formatNutritionValue(
            nutritionData.trans_fat,
            'trans_fat'
          ),
          className: styles['nutrition-row-border'],
        },
      ],
    },
    {
      label: nutritionKeyToLabel.cholesterol,
      firstSlotValue: formatNutritionValue(
        nutritionData.cholesterol,
        'cholesterol'
      ),
      secondSlotValue: formatNutritionValue(
        nutritionData.cholesterol_dv,
        'cholesterol_dv'
      ),
    },
    {
      label: nutritionKeyToLabel.sodium,
      firstSlotValue: formatNutritionValue(nutritionData.sodium, 'sodium'),
      secondSlotValue: formatNutritionValue(
        nutritionData.sodium_dv,
        'sodium_dv'
      ),
      className: styles['nutrition-row-border'],
    },
    {
      label: nutritionKeyToLabel.total_carb,
      firstSlotValue: formatNutritionValue(
        nutritionData.total_carb,
        'total_carb'
      ),
      secondSlotValue: formatNutritionValue(
        nutritionData.total_carb_dv,
        'total_carb_dv'
      ),
      subSections: [
        {
          label: nutritionKeyToLabel.fiber,
          firstSlotValue: formatNutritionValue(nutritionData.fiber, 'fiber'),
          secondSlotValue: formatNutritionValue(
            nutritionData.fiber_dv,
            'fiber_dv'
          ),
        },
        {
          label: nutritionKeyToLabel.sugars,
          firstSlotValue: formatNutritionValue(nutritionData.sugars, 'sugars'),
          subSections: [
            {
              label: `Included ${formatNutritionValue(
                nutritionData.added_sugars,
                'added_sugars'
              )} added sugars`,
              secondSlotValue: formatNutritionValue(
                nutritionData.added_sugars_dv,
                'added_sugars_dv'
              ),
              className: styles['nutrition-row-border'],
            },
          ],
        },
      ],
    },
    {
      label: nutritionKeyToLabel.protein,
      firstSlotValue: formatNutritionValue(nutritionData.protein, 'protein'),
      className: styles['nutrition-row-border-thick'],
    },
    {
      label: nutritionKeyToLabel.vitamin_d,
      secondSlotValue: formatNutritionValue(
        nutritionData.vitamin_d,
        'vitamin_d'
      ),
    },
  ];

  // per FDA requirements, these are optional, so if we have the values, show it
  if (nutritionData.vitamin_a && nutritionData.vitamin_a !== '0.00') {
    table.push({
      label: nutritionKeyToLabel.vitamin_a,
      secondSlotValue: formatNutritionValue(
        nutritionData.vitamin_a,
        'vitamin_a'
      ),
    });
  }
  if (nutritionData.vitamin_b6 && nutritionData.vitamin_b6 !== '0.00') {
    table.push({
      label: nutritionKeyToLabel.vitamin_b6,
      secondSlotValue: formatNutritionValue(
        nutritionData.vitamin_b6,
        'vitamin_b6'
      ),
    });
  }
  if (nutritionData.vitamin_c && nutritionData.vitamin_c !== '0.00') {
    table.push({
      label: nutritionKeyToLabel.vitamin_c,
      secondSlotValue: formatNutritionValue(
        nutritionData.vitamin_c,
        'vitamin_c'
      ),
    });
  }
  if (nutritionData.vitamin_e && nutritionData.vitamin_e !== '0.00') {
    table.push({
      label: nutritionKeyToLabel.vitamin_e,
      secondSlotValue: formatNutritionValue(
        nutritionData.vitamin_e,
        'vitamin_e'
      ),
    });
  }

  table.push(
    {
      label: nutritionKeyToLabel.calcium,
      secondSlotValue: formatNutritionValue(nutritionData.calcium, 'calcium'),
    },
    {
      label: nutritionKeyToLabel.iron,
      secondSlotValue: formatNutritionValue(nutritionData.iron, 'iron'),
    },
    {
      label: nutritionKeyToLabel.potassium,
      secondSlotValue: formatNutritionValue(
        nutritionData.potassium,
        'potassium'
      ),
      className: styles['nutrition-row-border'],
    }
  );

  return table;
};
